import React from 'react';

import { CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { IntegratedErps, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import {
  TenantIntegrationConnectionStatus,
  TenantIntegrationType,
} from '@recurrency/core-api-schema/dist/tenants/tenantUserDTO';
import { Alert, Card, Descriptions, notification } from 'antd';
import { theme } from 'theme';

import { NgrokStatusHistory } from 'pages/internal/tenants/TenantDetailsPage/tabs/NgrokStatusHistory';
import { DownloadNgrokAgentButton } from 'pages/setup/Integration/DownloadNgrokAgentButton';

import { Button } from 'components/Button';
import { AsyncButton } from 'components/Button/AsyncButton';
import { Container } from 'components/Container/Container';
import { CenteredLoader } from 'components/Loaders';
import { PageHeader } from 'components/PageHeader/PageHeader';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { captureAndShowError } from 'utils/error';

import { UpdateCredentialsModal } from './UpdateCredentialsModal';
import { UpdateTransactionApiModal } from './UpdateTransactionApiModal';

export const UpdateCredentialsPage = () => {
  const { activeTenant } = useGlobalApp();

  // Fetch current integration
  const {
    data: integrationsData,
    isLoading: integrationsLoading,
    reload: refetchIntegrations,
  } = useCoreApi(schemas.integrations.getTenantIntegrations, {
    pathParams: { tenantId: activeTenant.id },
  });

  const currentIntegration = integrationsData?.items[0];

  // Fetch ngrok agents (for P21 on-premise integrations)
  const { data: agentsData } = useCoreApi(schemas.integrations.getAllAgentsByTenant, {
    pathParams: { tenantId: activeTenant.id },
  });

  const hasAgents = agentsData && agentsData.items.length > 0;

  const handleTestConnection = async () => {
    const res = await coreApiFetch(schemas.tenants.validateTenantIntegrationConnection, {
      pathParams: { tenantId: activeTenant.id },
      bodyParams: {
        integrationType: TenantIntegrationType.DirectSql,
      },
    });

    if (res.data.status === TenantIntegrationConnectionStatus.Available) {
      notification.success({
        message: 'Connection Successful',
        description: `Your database credentials are valid and the connection is working properly. Validated at ${new Date(
          res.data.validatedAt,
        ).toLocaleString()}`,
      });
    } else {
      notification.error({
        message: 'Connection Failed',
        description:
          res.data.error || 'Unable to connect to the database. Please verify your credentials and try again.',
        duration: 10,
      });
    }
  };

  const handleTestApiConnection = async () => {
    try {
      const res = await coreApiFetch(schemas.tenants.validateTenantIntegrationConnection, {
        pathParams: { tenantId: activeTenant.id },
        bodyParams: {
          integrationType: TenantIntegrationType.P21TransactionApi,
        },
      });

      if (res.data.status === TenantIntegrationConnectionStatus.Available) {
        notification.success({
          message: 'API Connection Successful',
          description: `Your Transaction API credentials are valid and the connection is working properly. Validated at ${new Date(
            res.data.validatedAt,
          ).toLocaleString()}`,
        });
      } else {
        notification.error({
          message: 'API Connection Failed',
          description:
            res.data.error || 'Unable to connect to the Transaction API. Please verify your credentials and try again.',
          duration: 10,
        });
      }
    } catch (err) {
      captureAndShowError(err, 'Unable to test Transaction API connection');
    }
  };

  // Open update credentials modal
  const openUpdateCredentialsModal = () => {
    if (!currentIntegration) {
      return;
    }

    showAsyncModal(UpdateCredentialsModal, {
      integration: currentIntegration,
      onSuccess: refetchIntegrations,
    });
  };

  const openUpdateTransactionApiModal = () => {
    if (!currentIntegration) {
      return;
    }

    showAsyncModal(UpdateTransactionApiModal, {
      integration: currentIntegration,
      onSuccess: refetchIntegrations,
    });
  };

  if (integrationsLoading) {
    return <CenteredLoader />;
  }

  if (!currentIntegration) {
    return (
      <Container>
        <PageHeader title="Update Database Credentials" subtitle="Manage your integration credentials" />
        <Alert
          type="error"
          message="No Integration Found"
          description="No integration configured for this tenant. Please complete the initial setup first."
          showIcon
        />
      </Container>
    );
  }

  const getDatabaseType = () => {
    const syncSource = currentIntegration.syncSources?.read?.target;
    if (syncSource === 'mssql_db') return 'SQL Server (MSSQL)';
    if (syncSource === 'hana_db') return 'SAP HANA';
    return syncSource || 'Unknown';
  };

  const statusColor = currentIntegration.status === 'active' ? theme.colors.success[600] : theme.colors.warning[600];

  return (
    <Container>
      <PageHeader
        title="Update Database Credentials"
        subtitle="Update your integration credentials and test connectivity"
      />

      {/* Current Integration Info */}
      <Card title="Current Integration" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Database Type">{getDatabaseType()}</Descriptions.Item>
          <Descriptions.Item label="Integration Status">
            <span
              style={{
                color: statusColor,
                fontWeight: 'bold',
                textTransform: 'uppercase',
              }}
            >
              {currentIntegration.status}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Network Agent Configuration - Only for on-prem integrations with ngrok */}
      {hasAgents && (
        <Card title="Network Agent Configuration" style={{ marginBottom: 24 }}>
          <Alert
            type="info"
            message="Download and configure the Recurrency Agent to enable secure access to your on-premise database. If you've updated your database host or port, you may need to reconfigure your agent."
            style={{ marginBottom: 16 }}
            showIcon
          />
          {/* Agent Status History Timeline */}
          {agentsData?.items
            ?.filter((agent) => agent.statusHistory && agent.statusHistory.length > 0)
            .slice(0, 1)
            .map((agent) => (
              <NgrokStatusHistory key={agent.edgeId} history={agent.statusHistory!} agentInfo={agent} />
            ))}
          <DownloadNgrokAgentButton />
        </Card>
      )}

      {/* Credentials Management */}
      <Card title="Credentials Management" style={{ marginBottom: 24 }}>
        <Button
          type="primary"
          icon={<LockOutlined />}
          permissionsRequired={[TenantUserPermission.ActionEditSettings]}
          onClick={openUpdateCredentialsModal}
        >
          Update Credentials
        </Button>
        <AsyncButton
          key="test"
          icon={<CheckCircleOutlined />}
          permissionsRequired={[TenantUserPermission.ActionEditSettings]}
          onClick={handleTestConnection}
          className={css(`margin-left: 8px;`)}
        >
          Test Connection
        </AsyncButton>
        {activeTenant.erpType === IntegratedErps.P21 && (
          <div className={css(`margin-top: 12px;`)}>
            <Button
              type="primary"
              icon={<LockOutlined />}
              permissionsRequired={[TenantUserPermission.ActionEditSettings]}
              onClick={openUpdateTransactionApiModal}
            >
              Update Transaction API Credentials
            </Button>
            <AsyncButton
              key="test-api"
              icon={<CheckCircleOutlined />}
              permissionsRequired={[TenantUserPermission.ActionEditSettings]}
              onClick={handleTestApiConnection}
              className={css(`margin-left: 8px;`)}
            >
              Test API Connection
            </AsyncButton>
          </div>
        )}
      </Card>
    </Container>
  );
};
